import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import { useDropzone } from 'react-dropzone'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { ClientMessageType } from '@trustero/trustero-api-web/lib/tichatapi/agent_pb'
import { Spinner } from 'src/Throbber/Spinner'
import { StyledInputError } from 'src/components/Reusable/Inputs/TextInput/styles'
import { DynamicTextInput } from 'src/components/Reusable/Inputs/TextInput/TextInput'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import { capitalizeFirstLetter } from 'src/Utils/globalHelpers'
import {
  FileAttachmentSVG,
  PanelCollapseEastSvg,
  PanelCollapseWestSvg,
  PanelExpandEastSvg,
  PanelExpandWestSvg,
} from 'src/components/Icons/Basic'
import { DockablePanel } from 'src/components/Reusable/DockablePanel/DockablePanel'
import { FilterDropdownToggle } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.styles'
import { useAgentChat, useHasPlaybook } from 'src/context/Gatekeeper'
import { FlexRow } from 'src/components/Reusable/Flex'
import { CHAT_FILE_UPLOAD_ACCEPTED_MIME_TYPES } from 'src/components/Reusable/Forms/FileSelector/FileSelector.constants'
import { FileSelector } from 'src/components/Reusable/Forms/FileSelector/FileSelector'
import {
  correctAppleFiles,
  validateFiles,
} from 'src/components/Reusable/Forms/FileSelector/FileSelector.helpers'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { getMimeTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { CloseIcon } from 'src/components/Reusable/Inputs/FilePicker/FilePicker.styles'
import palette from 'src/designSystem/variables/palette'
import { ModalForm, ModalFormId } from 'src/components/ModalForms/ModalForm'
import {
  AskQuestionSuggestions,
  SuggestionType,
  TEMPLATE_SUGGESTIONS,
} from '../SecurityQuestionnaire/askQuestion/AskQuestion.components'
import {
  ChatPromptInputContainer,
  PromptSendButton,
  ChatCollapsiblePanelContainer,
  ChatCollapsiblePanelDirection,
  ChatCollapsiblePanelTitle,
  ChatCollapsiblePanelDescription,
  ChatCollapsiblePanelHeader,
  ChatCollapsiblePanelHeaderContainer,
  PanelIcon,
  ChatCollapsiblePanelContent,
  ChatCollapsiblePanelFooter,
  ChatPromptInputRow,
  ChatCollapsiblePanelTitleText,
  CollapsedContainer,
  AgentPromptSendButton,
  SuggestedPromptsDropdownMenu,
  SuggestedPromptsDropdownItem,
} from './trusteroIntelligence.styles'
import {
  Panel,
  usePanelVisibilityContext,
} from './trusteroIntelligence.context'
import { ownFileDragsOnly } from './trusteroIntelligence.helpers'
import { useArtifactPanelContext } from './chatpanel/chatPanel.context'
import {
  ChatConversationContainer,
  ChatConversationPanelContainer,
} from './conversation/chatConversation.styles'

export type ChatCollapsiblePanelProps = React.PropsWithChildren<{
  collapseDirection: ChatCollapsiblePanelDirection
  description?: string
  title: string
  shortTitle?: string
  titleActions?: React.ReactNode
  headerActions?: React.ReactNode
  footer?: React.ReactNode
  type: Panel
  noBorder?: boolean
  forceCollapsiblePanel?: boolean
}>

export const ChatCollapsiblePanel = ({
  children,
  collapseDirection,
  description,
  title,
  shortTitle,
  titleActions,
  headerActions,
  footer,
  type,
  noBorder = false,
  forceCollapsiblePanel = false,
}: ChatCollapsiblePanelProps): JSX.Element => {
  const {
    panels,
    setPanelVisibility,
    agentPanel,
    setAgentPanel,
    attached,
    setAttached,
  } = usePanelVisibilityContext()
  const isAgentChat = useAgentChat()
  const showAgentChat = isAgentChat && !forceCollapsiblePanel

  const isCollapsed = useMemo(() => !panels.includes(type), [panels, type])

  const toggleCollapse = useCallback(() => {
    setPanelVisibility(type, isCollapsed)
  }, [isCollapsed, setPanelVisibility, type])

  const collapseExpandIcon = useMemo(() => {
    if (showAgentChat) {
      return null
    }
    const icon =
      collapseDirection === 'east' ? (
        isCollapsed ? (
          <PanelExpandWestSvg />
        ) : (
          <PanelCollapseEastSvg />
        )
      ) : isCollapsed ? (
        <PanelExpandEastSvg />
      ) : (
        <PanelCollapseWestSvg />
      )
    return (
      <Tooltip
        id={`tooltip-collapse-expand-icon-${title}`}
        trigger={['hover', 'click']}
        tooltipBody={`${
          isCollapsed ? 'Expand' : 'Collapse'
        } ${capitalizeFirstLetter(title)}`}
        placement="bottom"
      >
        <CollapsedContainer>
          <PanelIcon onClick={toggleCollapse}>{icon}</PanelIcon>
          {shortTitle && isCollapsed && (
            <ChatCollapsiblePanelTitleText>
              {shortTitle}
            </ChatCollapsiblePanelTitleText>
          )}
        </CollapsedContainer>
      </Tooltip>
    )
  }, [
    collapseDirection,
    isCollapsed,
    toggleCollapse,
    title,
    shortTitle,
    showAgentChat,
  ])

  if (showAgentChat) {
    return (
      <DockablePanel
        title={shortTitle || title}
        description={description}
        headerActions={headerActions}
        footer={footer}
        show={agentPanel === type}
        onClose={() => setAgentPanel(undefined)}
        attached={attached}
        onAttach={() => setAttached(true)}
        onDetach={() => setAttached(false)}
      >
        {children}
      </DockablePanel>
    )
  }

  return (
    <ChatCollapsiblePanelContainer
      $collapseDirection={collapseDirection}
      $isCollapsed={isCollapsed}
      $hasShortTitle={!!shortTitle}
      $noBorder={noBorder}
    >
      <>
        {isCollapsed && collapseExpandIcon}
        <ChatCollapsiblePanelContent $isCollapsed={isCollapsed}>
          <ChatCollapsiblePanelHeaderContainer>
            <ChatCollapsiblePanelHeader>
              <ChatCollapsiblePanelTitle>
                {collapseExpandIcon}
                {title}
                {titleActions}
              </ChatCollapsiblePanelTitle>
              {headerActions}
            </ChatCollapsiblePanelHeader>
            {description && (
              <ChatCollapsiblePanelDescription>
                {description}
              </ChatCollapsiblePanelDescription>
            )}
          </ChatCollapsiblePanelHeaderContainer>
          {children}
        </ChatCollapsiblePanelContent>
        {!isCollapsed && footer && (
          <ChatCollapsiblePanelFooter>{footer}</ChatCollapsiblePanelFooter>
        )}
      </>
    </ChatCollapsiblePanelContainer>
  )
}

export type ChatPromptInputProps = {
  onSend: (
    prompt: string,
    messageType?: ClientMessageType,
    fileUploads?: File[],
  ) => void
  busy: boolean
  error?: string
  autoFocusKey?: string
  showSuggestions?: boolean
  isAgentChat?: boolean
  initiatePrompt?: string
  artifactPanelOpen?: boolean
  isNewChat?: boolean
  isCopilotMode?: boolean
  layoutKey?: string
}

const AGENT_SUGGESTIONS = TEMPLATE_SUGGESTIONS.map((text) => ({
  text,
  isHistory: false,
}))

// Appends incoming files, skipping any whose name is already attached.
const mergeFileUploads = (existing: File[], incoming: File[]): File[] => [
  ...existing,
  ...incoming.filter((file) => !existing.some((f) => f.name === file.name)),
]

export const ChatPromptInput = ({
  onSend,
  busy: tiBusy,
  error,
  autoFocusKey,
  showSuggestions,
  isAgentChat,
  initiatePrompt,
  artifactPanelOpen,
  isNewChat = false,
  isCopilotMode = false,
  layoutKey,
}: ChatPromptInputProps): JSX.Element => {
  const hasPlaybook = useHasPlaybook()
  const [prompt, setPrompt] = useState(initiatePrompt || '')
  const [suggestions, setSuggestions] = useState<SuggestionType[]>([])
  const { agentBusy } = useArtifactPanelContext()
  const [fileUploads, setFileUploads] = useState<File[]>([])
  const [fileUploadsFocusKey, setFileUploadsFocusKey] = useState(0)
  const [isSending, setIsSending] = useState(false)
  const hasMountedRef = useRef(false)
  const busy = agentBusy || tiBusy || isSending

  const onFilesDropped = useCallback((dropped: File[]) => {
    // correctAppleFiles drops unsupported types; reject the batch instead of
    // attaching the rest silently, as validateFiles does for oversized files.
    const supported = correctAppleFiles(
      dropped,
      CHAT_FILE_UPLOAD_ACCEPTED_MIME_TYPES,
    )
    const unsupported = dropped.filter((file) => !supported.includes(file))
    if (unsupported.length) {
      showInfoToast(
        `Unsupported file type: ${unsupported.map((f) => f.name).join(', ')}`,
      )
      return
    }
    const { validFiles, errors } = validateFiles(
      supported,
      CHAT_FILE_UPLOAD_ACCEPTED_MIME_TYPES,
    )
    if (errors) {
      showInfoToast(errors)
      return
    }
    setFileUploads((prev) => mergeFileUploads(prev, validFiles))
  }, [])

  // Same gating as the attach button. Dropzone owns the container's ref
  // (getRootProps overrides any ref passed alongside it), so reuse rootRef.
  const {
    getRootProps,
    isDragActive,
    rootRef: ref,
  } = useDropzone({
    onDrop: onFilesDropped,
    noClick: true,
    noKeyboard: true,
    disabled: !hasPlaybook || busy,
  })

  const onPromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (e.target.value === '\n') {
        setPrompt('')
      } else {
        setPrompt(e.target.value)
      }
      setSuggestions([])
    },
    [],
  )

  const onPromptSend = useCallback(async () => {
    if (prompt.trim() !== '') {
      setIsSending(true)
      try {
        await onSend(prompt, undefined, fileUploads)
      } finally {
        setIsSending(false)
      }
      setPrompt('')
      setSuggestions([])
      setFileUploads([])
    }
  }, [onSend, prompt, fileUploads])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!busy && e.key === 'Enter') {
        onPromptSend()
      }
    },
    [busy, onPromptSend],
  )

  const onMouseDown = useCallback(() => {
    if (showSuggestions && !prompt) {
      setSuggestions(
        TEMPLATE_SUGGESTIONS.map((text) => ({ text, isHistory: false })),
      )
    }
  }, [showSuggestions, prompt])

  const onBlur = useCallback(() => {
    setSuggestions([])
  }, [])

  const submitAndDismissFocus = useCallback(
    (suggestion?: string) => {
      if (suggestion) {
        onSend(suggestion)
        setPrompt('')
        setSuggestions([])
      }
    },
    [onSend],
  )

  useEffect(() => {
    if (initiatePrompt) {
      setPrompt(initiatePrompt)
    } else {
      setPrompt('')
    }
  }, [initiatePrompt])

  useEffect(() => {
    // Skip on initial mount to avoid double increment
    if (hasMountedRef.current) {
      setFileUploadsFocusKey((prev) => prev + 1)
    }
  }, [fileUploads])

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      // Use setTimeout to ensure the component is fully rendered before focusing
      setTimeout(() => {
        setFileUploadsFocusKey((prev) => prev + 1)
      }, 100)
    }
  }, [])

  const compositeAutoFocusKey = useMemo(
    () => `${autoFocusKey || ''}-${fileUploadsFocusKey}`,
    [autoFocusKey, fileUploadsFocusKey],
  )

  const onMaxRowsChange = useCallback(
    (isMaxRows: boolean) => {
      if (ref.current && isAgentChat) {
        ref.current.style.borderRadius = isMaxRows ? '16px' : '65px'
      }
    },
    [ref, isAgentChat],
  )

  const promptInputComponent = useMemo(
    () => (
      <ChatConversationContainer
        $isAgentChat={isAgentChat}
        $isCopilotMode={isCopilotMode}
        $isArtifactPanelOpen={artifactPanelOpen}
      >
        <FlexRow
          style={
            isCopilotMode
              ? { width: '100%', padding: '0 4px' }
              : { maxWidth: '880px', padding: '0 4px' }
          }
        >
          <ChatPromptInputContainer
            {...ownFileDragsOnly(getRootProps())}
            $isSuggestionsOpen={suggestions.length > 0}
            $isAgentChat={isAgentChat}
            $isDragActive={isDragActive}
          >
            {fileUploads.length > 0 && (
              <UploadedFiles
                fileUploads={fileUploads}
                setFileUploads={setFileUploads}
                busy={busy}
              />
            )}
            <ChatPromptInputRow>
              <DynamicTextInput
                placeholder="Ask Trustero Intelligence anything..."
                onChange={onPromptChange}
                onKeyDown={onKeyDown}
                onMouseDown={onMouseDown}
                onBlur={onBlur}
                value={prompt}
                autoFocus
                autoFocusKey={compositeAutoFocusKey}
                hasInitialText={!!initiatePrompt}
                layoutKey={layoutKey}
                maxRows={10}
                onMaxRowsChange={onMaxRowsChange}
                style={{ overflow: 'auto' }}
              />
              {isAgentChat && showSuggestions && (
                <SuggestedPromptsDropdown
                  suggestions={AGENT_SUGGESTIONS}
                  onSelect={submitAndDismissFocus}
                />
              )}
              {hasPlaybook && (
                <ChatFileUpload
                  fileUploads={fileUploads}
                  setFileUploads={setFileUploads}
                  busy={busy}
                />
              )}
              {busy ? (
                <Spinner size="s" color="primary" style={{ flexShrink: 0 }} />
              ) : isAgentChat ? (
                <AgentPromptSendButton onClick={onPromptSend} />
              ) : (
                <PromptSendButton onClick={onPromptSend} icon={faPaperPlane} />
              )}
            </ChatPromptInputRow>
            {!isAgentChat && suggestions.length > 0 && (
              <AskQuestionSuggestions
                suggestions={suggestions}
                askSuggestedQuestion={submitAndDismissFocus}
                highlightedIndex={-1}
                isTI={true}
              />
            )}
          </ChatPromptInputContainer>
        </FlexRow>
        {error && (
          <StyledInputError
            showError={!!error}
            style={{ textAlign: 'center', marginTop: 8 }}
          >
            We encountered an error
          </StyledInputError>
        )}
      </ChatConversationContainer>
    ),
    [
      isAgentChat,
      isCopilotMode,
      artifactPanelOpen,
      suggestions,
      busy,
      getRootProps,
      isDragActive,
      error,
      onPromptChange,
      onKeyDown,
      onMouseDown,
      onBlur,
      onPromptSend,
      showSuggestions,
      onMaxRowsChange,
      compositeAutoFocusKey,
      initiatePrompt,
      prompt,
      submitAndDismissFocus,
      fileUploads,
      hasPlaybook,
      layoutKey,
    ],
  )

  if (isNewChat) {
    return promptInputComponent
  }

  return (
    <ChatConversationPanelContainer
      $isAgentChat={isAgentChat}
      $isCopilotMode={isCopilotMode}
      $isArtifactPanelOpen={artifactPanelOpen}
      $noBottomSpace={true}
    >
      {promptInputComponent}
    </ChatConversationPanelContainer>
  )
}

const SuggestedPromptsDropdown = ({
  suggestions,
  onSelect,
}: React.PropsWithChildren<{
  suggestions: SuggestionType[]
  onSelect: (suggestion?: string) => void
}>): JSX.Element => {
  return (
    <Dropdown>
      <Dropdown.Toggle as={FilterDropdownToggle}>
        <p>Suggested Prompts</p>
      </Dropdown.Toggle>
      <SuggestedPromptsDropdownMenu>
        {suggestions.map((suggestion) => (
          <SuggestedPromptsDropdownItem
            key={suggestion.text}
            onClick={() => onSelect(suggestion.text)}
          >
            {suggestion.text}
          </SuggestedPromptsDropdownItem>
        ))}
      </SuggestedPromptsDropdownMenu>
    </Dropdown>
  )
}

const ChatFileUpload = ({
  fileUploads,
  setFileUploads,
  busy,
}: {
  fileUploads: File[]
  setFileUploads: (fileUploads: File[]) => void
  busy: boolean
}): JSX.Element => {
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [files, setFiles] = useState<File[]>([])

  const handleClick = useCallback(() => {
    if (busy) {
      return
    }
    setShowUploadModal(true)
  }, [busy])

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      setShowUploadModal(false)
      setFileUploads(mergeFileUploads(fileUploads, files))
      setFiles([])
    },
    [files, fileUploads, setFileUploads],
  )

  return (
    <div>
      <Tooltip
        id="tooltip-file-upload"
        trigger={['hover', 'focus']}
        tooltipBody="Select Files"
        placement="top"
        disabled={busy}
      >
        <FileAttachmentSVG
          onClick={handleClick}
          style={{ cursor: busy ? 'not-allowed' : 'pointer' }}
          width="24px"
          height="24px"
          fill={busy ? palette.neutral[400] : palette.neutral[600]}
        />
      </Tooltip>

      {showUploadModal && (
        <ModalForm
          show={true}
          hide={() => setShowUploadModal(false)}
          title="Select Files"
          submitText="Add to Chat"
          formId={ModalFormId.CHAT_FILE_UPLOAD}
          size="sm"
        >
          <form id={ModalFormId.CHAT_FILE_UPLOAD} onSubmit={onSubmit}>
            <FileSelector
              files={files}
              text="Click or drag and drop files here (PDF, Excel, Word, CSV, image files.)"
              onChange={setFiles}
              onClear={() => setFiles([])}
              isValid={true}
              allowMultiple={true}
              isLoading={false}
              errorMessage=""
              accept={CHAT_FILE_UPLOAD_ACCEPTED_MIME_TYPES}
            />
          </form>
        </ModalForm>
      )}
    </div>
  )
}

const UploadedFiles = ({
  fileUploads,
  setFileUploads,
  busy,
}: React.PropsWithChildren<{
  fileUploads: File[]
  setFileUploads: (fileUploads: File[]) => void
  busy: boolean
}>): JSX.Element => {
  const onRemove = useCallback(
    (index: number) => {
      setFileUploads(fileUploads.filter((_, i) => i !== index))
    },
    [fileUploads, setFileUploads],
  )

  return (
    <div style={{ overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflowX: 'auto',
          flexWrap: 'nowrap',
          alignItems: 'center',
          gap: 4,
          padding: 4,
          paddingBottom: 8,
        }}
      >
        {fileUploads.map((file, index) => (
          <UploadedFile
            file={file}
            onRemove={() => onRemove(index)}
            key={`uploaded-file-${index}-${file.name}`}
          />
        ))}
      </div>
      {busy && fileUploads.length > 0 && (
        <div style={{ fontSize: 14, color: palette.neutral[600] }}>
          Uploading and indexing files... (this may take a few seconds)
        </div>
      )}
    </div>
  )
}

const UploadedFile = ({
  file,
  onRemove,
}: {
  file: File
  onRemove: () => void
}): JSX.Element => {
  const Icon = getMimeTypeIcon(file.type)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        border: `1px solid ${palette.neutral[200]}`,
        borderRadius: 12,
        fontSize: 12,
        padding: 4,
        gap: 4,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <Icon width="16px" height="16px" />
      <span>{file.name}</span>
      <CloseIcon onClick={onRemove} style={{ flexShrink: 0 }} />
    </div>
  )
}
